/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import Header from './components/Header';
import ProductSearch from './components/ProductSearch';
import ProductDetails from './components/ProductDetails';
import SessionSummary from './components/SessionSummary';
import InvoicePanel from './components/InvoicePanel';
import PaymentModal from './components/PaymentModal';
import AdminDashboard from './components/admin/AdminDashboard';
import ReportsPage from './components/admin/ReportsPage';
import './App.css';

const API_URL = 'https://cake-shop-mern.onrender.com/api';

function BillingPage() {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState('');
  const [currentTime, setCurrentTime] = useState('');
  const [barcode, setBarcode] = useState('');
  const [qty, setQty] = useState(1);
  const [product, setProduct] = useState(null);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [productList, setProductList] = useState([]);
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [invoiceTotal, setInvoiceTotal] = useState(0);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentType, setPaymentType] = useState('cash');
  const [paidAmount, setPaidAmount] = useState(null);
  const [paymentError, setPaymentError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    updateDateTime();
    const interval = setInterval(updateDateTime, 1000);
    loadAllProducts();
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    calculateInvoiceTotal();
  }, [invoiceItems]);

  const updateDateTime = () => {
    const now = new Date();
    setCurrentDate(now.toLocaleDateString());
    setCurrentTime(now.toLocaleTimeString());
  };

  const loadAllProducts = async () => {
    try {
      const response = await fetch(`${API_URL}/product`);
      const data = await response.json();
      setProductList(data);
    } catch (err) {
      console.error(err);
    }
  };

  const searchProduct = async () => {
    if (!barcode.trim()) return;
    setProduct(null);
    setMessage('');
    try {
      const response = await fetch(`${API_URL}/product/barcode/${barcode}`);
      if (response.ok) {
        const data = await response.json();
        setProduct(data);
        setMessage('Product found');
        setMessageType('success');
      } else {
        setProduct(null);
        setMessage('Product not found');
        setMessageType('error');
      }
    } catch (err) {
      setProduct(null);
      setMessage('Product not found');
      setMessageType('error');
    }
  };

  const addToInvoice = () => {
    if (!product) {
      setMessage('No product selected');
      setMessageType('error');
      return;
    }
    if (qty <= 0 || qty > product.stockQty) {
      setMessage('Invalid quantity');
      setMessageType('error');
      return;
    }

    const price = product.type === 'fixed' ? product.price : product.pricePerKg;
    const existing = invoiceItems.find((i) => i.barcode === product.barcode);

    if (existing) {
      setInvoiceItems(
        invoiceItems.map((i) =>
          i.barcode === product.barcode
            ? { ...i, qty: i.qty + qty, amount: (i.qty + qty) * i.price }
            : i
        )
      );
    } else {
      setInvoiceItems([
        ...invoiceItems,
        {
          name: product.name,
          barcode: product.barcode,
          qty,
          price,
          amount: qty * price,
        },
      ]);
    }

    setQty(1);
    setBarcode('');
    setProduct(null);
  };

  const removeItem = (index) => {
    setInvoiceItems(invoiceItems.filter((_, i) => i !== index));
  };

  const clearInvoice = () => {
    setInvoiceItems([]);
    setInvoiceTotal(0);
    setBarcode('');
    setQty(1);
    setProduct(null);
  };

  const calculateInvoiceTotal = () => {
    const total = invoiceItems.reduce((sum, i) => sum + i.amount, 0);
    setInvoiceTotal(total);
  };

  const calculateTotalQty = () => {
    return invoiceItems.reduce((sum, i) => sum + i.qty, 0);
  };

  const generateInvoiceNumber = () => {
    const d = new Date();
    return `${d.getFullYear()}${d.getMonth() + 1}${d.getDate()}-${Math.floor(
      Math.random() * 10000
    )}`;
  };

  const openPaymentModal = () => {
    if (invoiceItems.length === 0) {
      setMessage('No items');
      setMessageType('error');
      return;
    }
    setPaymentType('cash');
    setPaidAmount(null);
    setPaymentError('');
    setShowPaymentModal(true);
  };

  const closePaymentModal = () => {
    setShowPaymentModal(false);
    setPaymentError('');
    setPaidAmount(null);
  };

  const computeChange = () => {
    const paid = paidAmount ?? 0;
    return Math.max(0, Math.round((paid - invoiceTotal) * 100) / 100);
  };

  const confirmPayment = async () => {
    setPaymentError('');
    if (paidAmount === null || isNaN(paidAmount)) {
      setPaymentError('Enter valid paid amount');
      return;
    }
    if (paidAmount < invoiceTotal) {
      setPaymentError('Paid amount insufficient');
      return;
    }

    setIsProcessing(true);

    try {
      for (const item of invoiceItems) {
        await fetch(`${API_URL}/product/reduce`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ barcode: item.barcode, qty: item.qty }),
        });
      }

      const invoicePayload = {
        date: new Date(),
        totalAmount: invoiceTotal,
        items: invoiceItems.map((i) => {
          const prod = productList.find((p) => p.barcode === i.barcode);
          return {
            productId: prod?._id,
            productName: i.name,
            qty: i.qty,
            unitPrice: i.price,
            amount: i.amount,
          };
        }),
      };

      await fetch(`${API_URL}/invoice/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(invoicePayload),
      });

      const paid = paidAmount ?? invoiceTotal;
      const change = Math.round((paid - invoiceTotal) * 100) / 100;
      printReceipt({ ...invoicePayload, paid, change });

      clearInvoice();
      setShowPaymentModal(false);
      setBarcode('');
      setQty(1);
      setProduct(null);
      setMessage(
        `Bill No: ${generateInvoiceNumber()} | Total: ${invoiceTotal} LKR | Change: ${change} LKR`
      );
      setMessageType('success');

      setTimeout(() => {
        setMessage('');
        document.getElementById('barcode')?.focus();
      }, 4000);
    } catch (err) {
      console.error(err);
      setPaymentError('Payment error');
      setMessage('Payment error');
      setMessageType('error');
    } finally {
      setIsProcessing(false);
    }
  };

  const printReceipt = (invoiceData) => {
    const header = `<div style="text-align:center;font-family:monospace;">
                      <h2>Cake Shop</h2>
                      <div>${invoiceData.date}</div><hr/>
                    </div>`;

    let itemsHtml = '<table style="width:100%; font-family:monospace;"><tbody>';

    invoiceData.items.forEach((it) => {
      const amount = it.amount ?? it.qty * (it.unitPrice ?? it.price ?? 0);
      const price = it.price ?? it.unitPrice ?? 0;
      itemsHtml += `<tr>
                      <td>${it.name}</td>
                      <td>${it.qty} x ${price.toFixed(2)}</td>
                      <td style="text-align:right">${amount.toFixed(2)}</td>
                    </tr>`;
    });

    itemsHtml += '</tbody></table>';

    const total = invoiceData.totalAmount ?? 0;
    const paid = invoiceData.paid ?? total;
    const change = invoiceData.change ?? Math.max(0, paid - total);

    const totals = `<hr/>
                    <div>Total: ${total.toFixed(2)} Paid: ${paid.toFixed(
      2
    )} Change: ${change.toFixed(2)}</div>
                    <hr/>`;

    const printHtml = `<html><head><title>Receipt</title></head><body>${header}${itemsHtml}${totals}</body></html>`;
    const w = window.open('', '_blank', 'width=350,height=600');
    if (!w) {
      alert('Allow popups');
      return;
    }
    w.document.write(printHtml);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 500);
  };

  const onBarcodeEnter = () => {
    searchProduct();
    setTimeout(() => {
      document.getElementById('qty')?.focus();
    }, 50);
  };

  const onBarcodeTab = (e) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      document.querySelector('[data-index="0"]')?.focus();
    }
  };

  const onQtyEnter = () => {
    addToInvoice();
    setTimeout(() => {
      document.getElementById('barcode')?.focus();
    }, 50);
  };

  const onInvoiceRowKey = (e, index) => {
    if (e.key === 'Enter') {
      openPaymentModal();
      setTimeout(() => {
        document.getElementById('barcode')?.focus();
      }, 50);
    } else if (e.key === 'Delete') {
      removeItem(index);
    } else if (e.key === 'ArrowUp' && index > 0) {
      document.querySelector(`[data-index="${index - 1}"]`)?.focus();
    } else if (e.key === 'ArrowDown' && index < invoiceItems.length - 1) {
      document.querySelector(`[data-index="${index + 1}"]`)?.focus();
    }
  };

  useEffect(() => {
    const handleGlobalShortcuts = (e) => {
      if (e.ctrlKey && e.key === 'c') {
        clearInvoice();
        e.preventDefault();
      } else if (e.ctrlKey && e.key === 'g') {
        openPaymentModal();
        e.preventDefault();
      } else if (e.key === 'F1') {
        e.preventDefault();
        window.location.href = '/admin/products';
      }
    };

    window.addEventListener('keydown', handleGlobalShortcuts);
    return () => window.removeEventListener('keydown', handleGlobalShortcuts);
  }, [invoiceItems]);

  return (
    <div className="billing-wrapper">
      <Header currentDate={currentDate} currentTime={currentTime} />

      <div className="billing-content">
        <div className="left-panel">
          <ProductSearch
            barcode={barcode}
            setBarcode={setBarcode}
            onSearch={searchProduct}
            onBarcodeEnter={onBarcodeEnter}
            onBarcodeTab={onBarcodeTab}
            message={message}
            messageType={messageType}
          />

          {product && (
            <ProductDetails
              product={product}
              qty={qty}
              setQty={setQty}
              onAddToInvoice={addToInvoice}
              onQtyEnter={onQtyEnter}
            />
          )}

          <SessionSummary
            itemCount={invoiceItems.length}
            totalQty={calculateTotalQty()}
            totalAmount={invoiceTotal}
          />
        </div>

        <InvoicePanel
          invoiceNumber={invoiceNumber}
          items={invoiceItems}
          total={invoiceTotal}
          onRemoveItem={removeItem}
          onRowKeyDown={onInvoiceRowKey}
          onCompleteSale={openPaymentModal}
          onClearAll={clearInvoice}
        />
      </div>

      <PaymentModal
        show={showPaymentModal}
        total={invoiceTotal}
        paymentType={paymentType}
        setPaymentType={setPaymentType}
        paidAmount={paidAmount}
        setPaidAmount={setPaidAmount}
        change={computeChange()}
        error={paymentError}
        onConfirm={confirmPayment}
        onCancel={closePaymentModal}
        isProcessing={isProcessing}
      />
    </div>
  );
}
function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<BillingPage />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/reports" element={<ReportsPage />} />
      </Routes>
    </Router>
  );
}
export default App;
